import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { AGE_BANDS, KB_KINDS, listMethodologyKb, upsertMethodologyKb } from "@/lib/clinic/methodology-kb.functions";

const KIND_LABELS: Record<string, string> = {
  definition: "تعريف",
  weakness_signs: "علامات الضعف",
  activities: "أنشطة",
  parent_phrases: "عبارات للأهل",
  parent_mistakes: "أخطاء الأهل",
  games_stories: "ألعاب وقصص",
  measurement: "قياس",
  prevention: "وقاية",
};

export const Route = createFileRoute("/_authenticated/admin/methodology-kb")({
  head: () => ({
    meta: [
      { title: "قاعدة المعرفة — منهج د. سارة" },
      { name: "description", content: "قاعدة المعرفة (KB) لتوليد خطط العمل" },
    ],
  }),
  component: Page,
});

function Page() {
  const listFn = useServerFn(listMethodologyKb);
  const upsertFn = useServerFn(upsertMethodologyKb);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin", "methodology-kb"], queryFn: () => listFn() });
  const [editing, setEditing] = useState<any | null>(null);

  const [filterPillar, setFilterPillar] = useState<string>("");
  const [filterAge, setFilterAge] = useState<string>("");

  async function save() {
    if (!editing) return;
    try {
      await upsertFn({ data: {
        id: editing.id,
        pillar_slug: editing.pillar_slug,
        age_band: editing.age_band,
        kind: editing.kind,
        content_md: editing.content_md ?? "",
        is_active: !!editing.is_active,
      }});
      toast.success("تم الحفظ");
      qc.invalidateQueries({ queryKey: ["admin", "methodology-kb"] });
      setEditing(null);
    } catch (e: any) {
      toast.error(e?.message ?? "فشل");
    }
  }

  const rows = (data?.rows ?? []).filter((r: any) =>
    (!filterPillar || r.pillar_slug === filterPillar) &&
    (!filterAge || r.age_band === filterAge)
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" dir="rtl">
      <div>
        <h1 className="font-display text-2xl font-extrabold">قاعدة المعرفة — منهج د. سارة</h1>
        <p className="mt-1 text-sm text-muted-foreground">تُستَخدم لإطعام مسودّات خطط العمل. المحتوى يبقى مسودّة حتى تفعّليه.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select value={filterPillar} onChange={(e) => setFilterPillar(e.target.value)} className="rounded-xl border border-input bg-background px-3 py-2 text-sm">
          <option value="">كل المحاور</option>
          {(data?.pillars ?? []).map((p: any) => (
            <option key={p.slug} value={p.slug}>{p.name_ar} ({p.slug})</option>
          ))}
        </select>
        <select value={filterAge} onChange={(e) => setFilterAge(e.target.value)} className="rounded-xl border border-input bg-background px-3 py-2 text-sm">
          <option value="">كل الأعمار</option>
          {AGE_BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">تحميل…</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs">
              <tr>
                <th className="p-2 text-start">المحور</th>
                <th className="p-2 text-start">العمر</th>
                <th className="p-2 text-start">النوع</th>
                <th className="p-2 text-start">مفعّل؟</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-2 font-mono text-xs">{r.pillar_slug}</td>
                  <td className="p-2">{r.age_band}</td>
                  <td className="p-2">{KIND_LABELS[r.kind] ?? r.kind}</td>
                  <td className="p-2">{r.is_active ? "نعم" : "—"}</td>
                  <td className="p-2 text-end">
                    <button onClick={() => setEditing(r)} className="rounded-full border border-input px-3 py-1 text-xs">تحرير</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => setEditing(null)}>
          <div className="flex-1 bg-foreground/30 backdrop-blur-sm" />
          <aside onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-2xl overflow-y-auto bg-card p-6 shadow-lift" dir="rtl">
            <h3 className="mb-4 font-display text-lg font-extrabold">
              {editing.pillar_slug} · {editing.age_band} · {KIND_LABELS[editing.kind] ?? editing.kind}
            </h3>
            <textarea
              rows={16}
              value={editing.content_md ?? ""}
              onChange={(e) => setEditing({ ...editing, content_md: e.target.value })}
              className="w-full rounded-xl border border-input bg-background px-3 py-2 font-mono text-xs"
            />
            <label className="mt-3 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
              مفعّل (يُستخدَم في توليد المسودّات)
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="rounded-full border border-input px-4 py-2 text-sm">إلغاء</button>
              <button onClick={save} className="rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">حفظ</button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

// Silence unused-import warnings from strict setups.
void KB_KINDS;